import React, { Fragment, Component } from "react";
import { Button, FormControl, Row, Col, ControlLabel } from "react-bootstrap";
import "react-tabulator/lib/styles.css"; // required styles
import "react-tabulator/lib/css/tabulator.min.css"; // theme
import { ReactTabulator } from "react-tabulator";
import { confirmAlert } from "react-confirm-alert"; // Import
import "react-confirm-alert/src/react-confirm-alert.css"; // Import css
import Header from "../header";
import LoaderButton from "../LoaderButton";
import {
  deleteRepository,
  addRepoData,
  getRepositories,
  getAllClients,
  getRepositoriesByClient,
  saveNewClient,
  getDepartments,
} from "../../libs/API";
import "./managerepository.css";
import { role } from "../enum";
import ManageDepartmentsPopup from "./manageDepartmentsPopup";
import EditRepositoryPopup from "./editRepositoryPopup";
import confirmMarkBillable from "./confirmBillable";

// Tabulator formatters return DOM nodes so department names are rendered as text, not HTML.
const departmentFormatter = (cell) => {
  if (cell.getRow().getData().departmentIsSystem) {
    const badge = document.createElement("span");
    badge.className = "dept-not-assigned-badge";
    badge.textContent = cell.getValue() || "Dept Not Assigned";
    return badge;
  }
  return document.createTextNode(cell.getValue() || "");
};

const billableFormatter = (cell) => {
  const checkbox = document.createElement("input");
  checkbox.type = "checkbox";
  checkbox.checked = !!cell.getValue();
  checkbox.disabled = true;
  checkbox.setAttribute("aria-label", "Billable");
  return checkbox;
};

class ManageRepository extends Component {
  gridRepoRef = null;
  constructor(props) {
    super(props);
    this.state = {
      repoData: [],
      loadingRepos: false,
      clients: [],
      repositoryOwner: "",
      folderName: "",
      s3Path: "",
      message: "",
      repositoryId: 0,
      isLoading: false,
      selectedClient: "",
      clientId: this.props.props.clientId,
      showAddClientForm: false,
      newClientName: "",
      hasManageClientsUserRole: false,
      departments: [],
      departmentId: "",
      departmentError: false,
      billable: false,
      showDepartmentsPopup: false,
      editingRepo: null,
    };
    // console.log("ManageRepository", props.props.token);
  }
  async componentDidMount() {
    var hasManageClientsUserRole = this.props.props.groups.includes(
      role.documentSync_ManageClients,
    );

    if (
      this.state.clientId &&
      this.state.clientId > 0 &&
      !hasManageClientsUserRole
    ) {
      await this.getAllRepositories(this.state.clientId);
    } else {
      await this.getAllClients(hasManageClientsUserRole);
    }

    if (this.state.clientId && this.state.clientId > 0) {
      await this.loadDepartments(this.state.clientId);
    }
  }

  getCurrentClientId = () => this.state.selectedClient || this.state.clientId;

  // Single-client users never load the client list, so fall back to the grid rows.
  getCurrentClientName = () => {
    const currentClientId = String(this.getCurrentClientId());
    const client = this.state.clients.find(
      (c) => String(c.clientId) === currentClientId,
    );
    if (client) return client.name;
    return this.state.repoData[0]?.clientName || "";
  };

  handleBillableChange = (checked) => {
    if (checked) {
      confirmMarkBillable(() => this.setState({ billable: true }));
    } else {
      this.setState({ billable: false });
    }
  };

  // Every grid load goes through here so the spinner shows over the grid.
  fetchRepositories = async (clientId) => {
    this.setState({ loadingRepos: true });
    try {
      return await getRepositoriesByClient(clientId);
    } finally {
      this.setState({ loadingRepos: false });
    }
  };

  getAllRepositories = async (clientId) => {
    const data = await this.fetchRepositories(clientId);
    if (data) {
      this.setState({ repoData: data });
    }
  };

  reloadRepositories = async () => {
    const repositories = await this.fetchRepositories(this.getCurrentClientId());
    this.setState({ repoData: repositories });
    if (this.gridRepoRef && this.gridRepoRef.table) {
      this.gridRepoRef.table.setData(repositories);
    }
  };

  loadDepartments = async (clientId) => {
    try {
      const departments = await getDepartments(clientId);
      this.setState({ departments });
    } catch (error) {
      this.setState({ departments: [], message: error.message });
    }
  };

  resetNewRepoForm = () => {
    this.setState({
      repositoryOwner: "",
      folderName: "",
      departmentId: "",
      departmentError: false,
      billable: false,
    });
  };

  getAllClients = async (hasManageClientsUserRole) => {
    getAllClients().then((data) => {
      if (data) {
        this.setState({ clients: data, hasManageClientsUserRole });
      }
    });
  };

  toggleAddClientForm = () => {
    this.setState((prevState) => ({
      showAddClientForm: !prevState.showAddClientForm,
    }));
  };

  handleNewClientNameChange = (event) => {
    this.setState({ newClientName: event.target.value });
  };

  saveNewClient = async () => {
    try {
      // Call the API function to save the new client
      const newClient = await saveNewClient(this.state.newClientName);

      // Update the clients state with the new client
      this.setState((prevState) => ({
        clients: [...prevState.clients, newClient].sort((a, b) => {
          if (!a.name) return 1; // Move undefined or null `name` to the end
          if (!b.name) return -1; // Move undefined or null `name` to the end
          return a.name.localeCompare(b.name); // Sort by `name` in ascending order
        }),
        newClientName: "",
        showAddClientForm: false,
      }));
    } catch (error) {
      console.error("Error saving new client:", error);
      // Handle the error appropriately
    }
  };

  addNewRow = async (event) => {
    event.preventDefault();

    const currentClientId = this.getCurrentClientId();

    if (!currentClientId || currentClientId < 1) {
      this.setState({ message: "Please select a client." });
      return;
    }

    // Validate before calling the API: AddRepoData creates the folder before checking the department.
    if (!this.state.departmentId) {
      this.setState({ departmentError: true, message: "" });
      return;
    }

    this.setState({ isLoading: true, message: "" });

    try {
      const newRow = {
        repositoryOwner: this.state.repositoryOwner,
        folderName: this.state.folderName,
        directoryPath: this.state.s3Path,
        repositoryId: this.state.repositoryId,
        clientId: currentClientId,
        departmentId: Number(this.state.departmentId),
        billable: this.state.billable,
      };

      const data = await addRepoData(newRow);

      if (data.errorMessage.length === 0) {
        this.resetNewRepoForm();
        await this.reloadRepositories();
      } else {
        this.setState({ message: data.errorMessage });
      }
    } catch (error) {
      this.setState({ message: error.message });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  showAlert = (cell) => {
    confirmAlert({
      title: "Confirm to Delete",
      message: "Are you sure to delete this.",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            let repositoryId = cell.getRow().getData().repositoryId;
            deleteRepository(repositoryId).then((data) => {
              if (data.status === 200) {
                cell.getRow().delete();
              }
            });
          },
        },
        {
          label: "No",
        },
      ],
    });
  };

  updateData = async (e, cell) => {
    e.preventDefault();
    if (e.target.innerText === "Edit") {
      this.setState({ editingRepo: cell.getRow().getData(), message: "" });
    } else if (e.target.innerText === "Delete") {
      this.showAlert(cell);
    }
  };

  //handleClientChange = (event) => {
  //  this.setState({ selectedClient: event.target.value });
  //}

  handleClientChange = async (event) => {
    const selectedClientId = event.target.value;
    this.setState({
      clientId: selectedClientId,
      selectedClient: selectedClientId,
      isLoading: true,
      departments: [],
      departmentId: "",
      departmentError: false,
      billable: false,
      message: "",
    });

    try {
      if (selectedClientId) {
        await this.loadDepartments(selectedClientId);
        const repositories = await this.fetchRepositories(selectedClientId);
        this.setState({ repoData: repositories });

        // Update the tabular grid with the new data
        if (this.gridRepoRef && this.gridRepoRef.table) {
          this.gridRepoRef.table.setData(repositories);
        }
      } else {
        // If no client is selected, fetch all repositories
        //await this.getAllRepositories();
      }
    } catch (error) {
      console.error("Error fetching repositories:", error);
      this.setState({
        message: "Failed to fetch repositories. Please try again.",
      });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  renderRepoTabulator() {
    let openButton = function () {
      let button =
        "<div><button type='button'class='btn btn-outline-secondary btn-sm updatedeletebuttons' >Edit</button> <button type='button'class='btn btn-outline-secondary btn-sm updatedeletebuttons' >Delete</button></div>";
      return button;
    };
    const currentClientId = this.getCurrentClientId();
    const selectableDepartments = this.state.departments.filter(
      (d) => !d.isSystem,
    );
    return (
      <div className="managerepo-content">
        <div className="header-container">
          <h4>Manage Repositories</h4>

          <div className="client-selection-container">
            {this.state.clientId &&
            this.state.clientId > 0 &&
            !this.state.hasManageClientsUserRole ? null : (
              <>
                <div className="client-field">
                  <label htmlFor="clientSelect" className="client-field-label">
                    Client
                  </label>
                  <FormControl
                    componentClass="select"
                    id="clientSelect"
                    value={this.state.selectedClient}
                    onChange={this.handleClientChange}
                    className="client-dropdown"
                  >
                    <option value="">Select a client</option>
                    {this.state.clients.map((client) => (
                      <option key={client.clientId} value={client.clientId}>
                        {client.name}
                      </option>
                    ))}
                  </FormControl>
                </div>
                <button
                  type="button"
                  onClick={this.toggleAddClientForm}
                  className="btn btn-outline-secondary add-client-button"
                  title="Add client"
                  aria-label="Add client"
                >
                  <span className="green-plus-sign" aria-hidden="true">
                    +
                  </span>
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => this.setState({ showDepartmentsPopup: true })}
              disabled={!currentClientId}
              className="btn brand-button manage-departments-button"
            >
              Manage Departments
            </button>
          </div>

          {this.state.showAddClientForm && (
            <div className="add-client-form">
              <FormControl
                type="text"
                value={this.state.newClientName}
                onChange={this.handleNewClientNameChange}
                placeholder="Enter client name"
              />
              <Button onClick={this.saveNewClient} className="brand-button">
                Save
              </Button>
              <Button onClick={this.toggleAddClientForm}>Cancel</Button>
            </div>
          )}
        </div>

        <br />
        <div className="repo-grid-wrapper" aria-busy={this.state.loadingRepos}>
          {this.state.loadingRepos && (
            <div className="repo-grid-loading">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading repositories...</span>
              </div>
            </div>
          )}
          <ReactTabulator
            id="table"
            style={{
              marginLeft: "5px",
            }}
            columns={[
              {
                title: "Repository Owner",
                field: "repositoryOwner",
                headerFilter: "input",
              },
              {
                title: "Repository Name",
                field: "folderName",
                headerFilter: "input",
              },
              {
                title: "S3 Path",
                field: "s3Path",
                headerFilter: "input",
              },
              {
                title: "Department",
                field: "departmentName",
                headerFilter: "input",
                formatter: departmentFormatter,
              },
              {
                title: "Billable",
                field: "billable",
                hozAlign: "center",
                formatter: billableFormatter,
              },
              {
                title: "",
                headerSort: false,
                formatter: openButton,
                hozAlign: "center",
                cellClick: (e, cell) => {
                  this.updateData(e, cell);
                },
              },
            ]}
            ref={(ref) => (this.gridRepoRef = ref)}
            data={this.state.repoData}
            options={{
              height: "60vh",
              pagination: "local",
              paginationSize: 15,
            }}
          />
        </div>
        <ControlLabel className="errorLabel">{this.state.message}</ControlLabel>
        <div className="addnew-content">
          <form onSubmit={this.addNewRow}>
            <Row>
              <Col sm={3}>
                <label htmlFor="repoOwner" className="form-field-label">
                  Repository Owner:
                </label>
                <FormControl
                  className="input-box"
                  id="repoOwner"
                  type="input"
                  value={this.state.repositoryOwner}
                  onChange={(e) =>
                    this.setState({ repositoryOwner: e.target.value })
                  }
                  required
                />
              </Col>
              <Col sm={3}>
                <label htmlFor="folderName" className="form-field-label">
                  Repository Name:
                </label>
                <FormControl
                  className="input-box"
                  type="input"
                  id="folderName"
                  value={this.state.folderName}
                  onChange={(e) =>
                    this.setState({ folderName: e.target.value })
                  }
                  required
                />
              </Col>
              <Col sm={2}>
                <label htmlFor="departmentId" className="form-field-label">
                  Department: <span className="required-mark">*</span>
                </label>
                <FormControl
                  className={
                    "input-box" +
                    (this.state.departmentError ? " field-invalid" : "")
                  }
                  componentClass="select"
                  id="departmentId"
                  value={this.state.departmentId}
                  aria-required="true"
                  aria-invalid={this.state.departmentError}
                  aria-describedby={
                    this.state.departmentError
                      ? "departmentId-error"
                      : undefined
                  }
                  onChange={(e) =>
                    this.setState({
                      departmentId: e.target.value,
                      departmentError: false,
                    })
                  }
                >
                  <option value="">Select a department</option>
                  {selectableDepartments.map((d) => (
                    <option key={d.departmentId} value={String(d.departmentId)}>
                      {d.departmentName}
                    </option>
                  ))}
                </FormControl>
                {this.state.departmentError && (
                  <div id="departmentId-error" className="field-error">
                    Department is required.
                  </div>
                )}
                <div className="billable-field">
                  <input
                    type="checkbox"
                    id="newRepoBillable"
                    checked={this.state.billable}
                    onChange={(e) => this.handleBillableChange(e.target.checked)}
                  />
                  <label htmlFor="newRepoBillable">Billable</label>
                </div>
              </Col>
              {/* <Col sm={3}>
                S3 Path:
                <FormControl
                  className="input-box"
                  type="input"
                  onChange={(e) => this.setState({ s3Path: e.target.value })}
                />
              </Col> */}
              <Col sm={2}>
                <LoaderButton
                  block
                  bsClass="btn"
                  disabled={this.state.isLoading}
                  type="submit"
                  isLoading={this.state.isLoading}
                  text="Add New Repository"
                  loadingText="Adding Repository..."
                  className="addNewButton brand-button"
                />
              </Col>
            </Row>
          </form>
        </div>
        <ManageDepartmentsPopup
          show={this.state.showDepartmentsPopup}
          onHide={() => this.setState({ showDepartmentsPopup: false })}
          clientId={currentClientId}
          clientName={this.getCurrentClientName()}
          departments={this.state.departments}
          repositories={this.state.repoData}
          onChanged={() =>
            Promise.all([
              this.loadDepartments(currentClientId),
              this.reloadRepositories(),
            ])
          }
        />
        <EditRepositoryPopup
          show={!!this.state.editingRepo}
          onHide={() => this.setState({ editingRepo: null })}
          repository={this.state.editingRepo}
          departments={this.state.departments}
          onSaved={this.reloadRepositories}
        />
      </div>
    );
  }

  render() {
    return (
      <Fragment>
        <div className="managerepo-main">
          <Header props={this.props.props} history={this.props.history} />
          {this.renderRepoTabulator()}
        </div>
      </Fragment>
    );
  }
}

export default ManageRepository;
